import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PlusIcon, PencilIcon, TrashIcon, LockIcon } from "lucide-react";
import { CreateTaxRateDialog } from "./CreateTaxRateDialog";
import { EditTaxRateDialog } from "./EditTaxRateDialog";
import { AuditCell } from "./AuditCell";
import { ConfirmActionDialog } from "./ConfirmActionDialog";

interface TaxRateRow {
  id: string;
  organisationId: string | null;
  code: string;
  name: string;
  rate: number;
  source: "org" | "country";
  isReadOnly: boolean;
  createdAt: string | null;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface TaxRatesTableProps {
  organisationId: string;
}

export function TaxRatesTable({ organisationId }: TaxRatesTableProps) {
  const [taxRates, setTaxRates] = useState<TaxRateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TaxRateRow | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaxRateRow | null>(null);

  async function fetchTaxRates() {
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await api.GET("/v1/tax-rates", {
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (apiError) {
      setError("Failed to load tax rates.");
    } else {
      setTaxRates(((data as any)?.data as TaxRateRow[]) ?? []);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchTaxRates();
  }, [organisationId]);

  async function handleDelete(id: string) {
    setDeleting(id);
    const { error: apiError } = await api.DELETE("/v1/tax-rates/{id}", {
      params: { path: { id } },
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (apiError) {
      setError("Failed to delete tax rate.");
    } else {
      setTaxRates((prev) => prev.filter((t) => t.id !== id));
    }
    setDeleting(null);
    setDeleteTarget(null);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Tax Rates</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <PlusIcon className="size-4" />
          Create Tax Rate
        </Button>
      </div>

      {error && <p className="text-destructive mb-4 text-sm">{error}</p>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Last modified</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {taxRates.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground h-24 text-center">
                  No tax rates configured.
                </TableCell>
              </TableRow>
            ) : (
              taxRates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-mono font-medium">{t.code}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{t.name}</span>
                      {t.source === "country" && (
                        <Badge variant="outline" className="text-muted-foreground font-normal">
                          Country rate
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{t.rate}%</TableCell>
                  <TableCell>
                    {t.source === "org" ? (
                      <AuditCell
                        createdAt={t.createdAt ?? ""}
                        createdBy={t.createdBy}
                        updatedAt={t.updatedAt ?? ""}
                        updatedBy={t.updatedBy}
                      />
                    ) : (
                      <span className="text-muted-foreground text-xs">Platform-managed</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {t.isReadOnly ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex items-center justify-center size-8 text-muted-foreground"
                              aria-label="Read-only platform default"
                            >
                              <LockIcon className="size-4" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Platform-managed default — cannot be edited
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={() => setEditing(t)}
                          >
                            <PencilIcon className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            disabled={deleting === t.id}
                            onClick={() => setDeleteTarget(t)}
                          >
                            {deleting === t.id ? (
                              <Spinner className="size-4" />
                            ) : (
                              <TrashIcon className="size-4" />
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <CreateTaxRateDialog
        organisationId={organisationId}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={fetchTaxRates}
      />

      <EditTaxRateDialog
        organisationId={organisationId}
        taxRate={editing}
        open={editing !== null}
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        onUpdated={fetchTaxRates}
      />

      <ConfirmActionDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete this tax rate?"
        description={
          <>Deleting <span className="font-mono font-medium">{deleteTarget?.code}</span> — {deleteTarget?.name} cannot be undone.</>
        }
        confirmLabel="Delete"
        pending={!!deleting}
        onConfirm={() => { if (deleteTarget) handleDelete(deleteTarget.id); }}
      />
    </div>
  );
}
